import { colorCss } from "./colorCss";

const BOTTOM_OFFSET = 4;
const COLOR_OFFSET = 6;
const COLOR_FLOATS = 4;
const ALPHA_OFFSET = COLOR_OFFSET + COLOR_FLOATS - 1;

const isSameColor = (instances, offset, other) => {
  for (let index = COLOR_OFFSET; index < COLOR_OFFSET + COLOR_FLOATS; index += 1) {
    if (instances[offset + index] !== instances[other + index]) return false;
  }

  return true;
};

/**
 * Canvas 2D version of fillRenderer.draw: following segments of one colour
 * become one polygon (the upper line forwards, the lower one back), so the
 * shared edges leave no seams. Transparent segments (no values) break it.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {Float32Array} instances - [x0, top0, x1, top1, bottom0, bottom1, r, g, b, a] per segment
 * @param {number} floatsPerInstance
 * @param {object} params - see fillRenderer.draw
 */
export const paintFill = (context, instances, floatsPerInstance, { viewport, xTransform, yTransform }) => {
  const [xScale, xOffset] = xTransform;
  const [yScale, yOffset] = yTransform;
  const toX = (value) => value * xScale + xOffset;
  const toY = (value) => value * yScale + yOffset;
  let run = [];

  const fillRun = () => {
    if (!run.length) return;

    const first = run[0];

    context.beginPath();
    context.moveTo(toX(instances[first]), toY(instances[first + 1]));
    run.forEach((offset) => context.lineTo(toX(instances[offset + 2]), toY(instances[offset + 3])));
    [...run].reverse().forEach((offset) => {
      context.lineTo(toX(instances[offset + 2]), toY(instances[offset + BOTTOM_OFFSET + 1]));
      context.lineTo(toX(instances[offset]), toY(instances[offset + BOTTOM_OFFSET]));
    });
    context.closePath();
    context.fillStyle = colorCss(instances, first + COLOR_OFFSET);
    context.fill();
    run = [];
  };

  for (let offset = 0; offset < instances.length; offset += floatsPerInstance) {
    const isVisible =
      instances[offset + ALPHA_OFFSET] > 0 &&
      Math.max(toX(instances[offset]), toX(instances[offset + 2])) >= 0 &&
      Math.min(toX(instances[offset]), toX(instances[offset + 2])) <= viewport[0];
    const previous = run[run.length - 1];
    const continuesRun =
      previous !== undefined &&
      instances[offset] === instances[previous + 2] &&
      isSameColor(instances, offset, previous);

    if (!isVisible || !continuesRun) fillRun();
    if (isVisible) run.push(offset);
  }

  fillRun();
};
